import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, KeyIcon } from '@heroicons/react/24/outline';
import Logo from './Logo';
import BookCall from './BookCall';

const NAVIGATION = [
  { name: 'The Assessment', href: '/assessment' },
  { name: 'What You Discover', href: '/what-you-discover' },
  { name: 'About', href: '/about' },
  { name: 'For Advisors', href: '/advisors' },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-cream transition-colors duration-300 ${
        isScrolled ? 'border-b border-secondary-200' : 'border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14">
        <div className="flex justify-between items-center gap-10 h-20 lg:h-24">
          <Link to="/" className="flex items-center" aria-label="The Family Legacy Bridge — home">
            <Logo className="h-11 lg:h-14 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAVIGATION.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-[13px] tracking-wide whitespace-nowrap transition-colors duration-200 ${
                  isActive(item.href)
                    ? 'text-secondary-900'
                    : 'text-secondary-500 hover:text-secondary-900'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <span className="h-6 w-px bg-secondary-200" aria-hidden="true" />
            <div className="flex items-center gap-6">
              <Link
                to="/evaluation"
                className="inline-flex items-center text-[13px] font-medium text-primary-700 hover:text-primary-900 whitespace-nowrap transition-colors duration-200"
              >
                <KeyIcon className="w-4 h-4 mr-1.5 opacity-70" />
                Begin your assessment
              </Link>
              <BookCall size="sm">Request a conversation</BookCall>
            </div>
          </nav>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden inline-flex items-center justify-center p-2 text-secondary-800"
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="lg:hidden bg-cream border-t border-secondary-200">
          <div className="px-6 py-8 space-y-1">
            {NAVIGATION.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block py-3 text-lg font-serif transition-colors ${
                  isActive(item.href)
                    ? 'text-secondary-900'
                    : 'text-secondary-600 hover:text-secondary-900'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <div className="pt-6 space-y-3">
              <Link
                to="/evaluation"
                className="flex items-center justify-center w-full rounded border border-primary-300 text-primary-800 hover:bg-primary-50 transition-colors duration-200 px-5 py-3.5 text-[15px] font-medium"
              >
                <KeyIcon className="w-4 h-4 mr-2 opacity-70" />
                Begin your assessment
              </Link>
              <BookCall size="md" className="w-full">
                Request a conversation
              </BookCall>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
